import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Popconfirm, Spin } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useTreatmentServiceDeclineTreatmentPlanRecipeEndpoint,
  useTreatmentServiceDispenseTreatmentPlanRecipeEndpoint,
  useTreatmentServiceGetTreatmentPlanRecipeEndpoint,
  useTreatmentServiceGetTreatmentPlanRecipeEndpointKey,
  useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey
} from 'src/api/queries'
import { ApiError, RecipeStatus } from 'src/api/requests'
import { message, PageBreadcrumbs } from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import {
  PrescriptionDetailsCard,
  PrescriptionItemsCard,
  PrescriptionPersonDetails
} from '../components'

export const PrescriptionDetailsPage = () => {
  const { t } = useLocales()

  const { treatmentPlanId, id } = useParams()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { data, isLoading } = useTreatmentServiceGetTreatmentPlanRecipeEndpoint(
    {
      id: String(treatmentPlanId),
      recipeId: String(id)
    }
  )

  const { mutate: decline, isPending: isDeclining } =
    useTreatmentServiceDeclineTreatmentPlanRecipeEndpoint({
      onSuccess: () => {
        message.success(t('recipes.success_declined'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeEndpointKey]
        })
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const { mutate: dispense, isPending: isDispensing } =
    useTreatmentServiceDispenseTreatmentPlanRecipeEndpoint({
      onSuccess: () => {
        message.success(t('recipes.success_dispensed'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeEndpointKey]
        })
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const pageTitle = data?.qrCode
    ? `${t('menu.prescription')} - ${data?.id}`
    : `${t('menu.prescription')} - ${getFullName(data?.patient?.lastName, data?.patient?.firstName)}`

  const breadcrumbItems = [
    {
      title: t('menu.prescriptions')
    },
    {
      title: pageTitle
    }
  ]

  const onBackClick = () => {
    navigate(-1)
  }

  const onDispense = (id: string, treatmentPlanId: string) => {
    dispense({ treatmentPlanId, recipeId: id })
  }

  const onDecline = (id: string, treatmentPlanId: string) => {
    decline({ treatmentPlanId, recipeId: id })
  }

  if (isLoading) return <Spin spinning fullscreen />

  return (
    <PageBreadcrumbs items={breadcrumbItems} title={pageTitle}>
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBackClick}>
            {t('common.back')}
          </Button>

          {data?.status === RecipeStatus.NEW && (
            <Flex gap={8}>
              <Popconfirm
                title={t('recipes.decline_confirm_title')}
                description={t('common.delete_description')}
                onConfirm={() => onDecline(String(id), String(treatmentPlanId))}
              >
                <Button
                  loading={isDispensing || isDeclining}
                  danger
                  type="primary"
                >
                  {t('transfer.reject')}
                </Button>
              </Popconfirm>

              <Popconfirm
                title={t('recipes.dispense_confirm_title')}
                description={t('common.delete_description')}
                onConfirm={() =>
                  onDispense(String(id), String(treatmentPlanId))
                }
              >
                <Button loading={isDispensing || isDeclining} type="primary">
                  {t('roles.confirm')}
                </Button>
              </Popconfirm>
            </Flex>
          )}
        </Flex>

        <PrescriptionDetailsCard data={data} />

        {data?.patient && <PrescriptionPersonDetails patient={data.patient} />}

        {!!data?.medicationDispenses?.length && (
          <PrescriptionItemsCard
            medicationDispenses={data.medicationDispenses}
          />
        )}
      </Flex>
    </PageBreadcrumbs>
  )
}
